// Job chip filters for the Metrics Jobs and Tasks & Results tabs: tag,
// job column and job field values, each source picked in an "Add filter"
// <select>, its values then chosen in a search list (lib/metrics/
// search_list.js) with "All containing" terms and exact picks. Sources AND
// together; a source's terms and picks match any of them.
//
// ---- Sources and URL params -------------------------------------------
//
//   source key (the picker <option> value, also the server's facet key):
//     'tag'            tags[]=<value>
//     'col:<id>'       columns[]=<id>:<value>
//     'field:<name>'   fields[]=<url-encoded normalized name>:<value>
//   <value> is an exact pick, normalized (trimmed, lower case; the server
//   compares LOWER(TRIM(value))), or ~<words> for a term. Older links carry
//   exact values in their stored spelling: they restore as picks, labelled
//   once the list answers. Exclusions are not offered for job chips.
//
// ---- Usage --------------------------------------------------------------
//
//   const chips = new JobChipFilters({
//     picker,            // the source <select> ('' = none chosen)
//     anchor,            // element the value search list renders into
//     urls: { tag, column, field },   // the metrics/jobs *_values endpoints
//     scope: 'jobs',     // or 'results': what n counts (scope=results)
//     baseParams: () => URLSearchParams,  // range + the tab's other filters
//     onChange: () => {},  // a source's filter changed: re-render, reload
//     onLabels: () => {}   // labels or term counts arrived: re-render chips
//   })
//   chips.initFromUrl(urlSearchParams)
//   chips.appendParams(p, except)   tags[]/columns[]/fields[] (except: a key)
//   chips.viewParams(params)        adds tags/columns/fields arrays
//   chips.chips()                   descriptors for renderFilterChips
//   chips.drillParams(p)            exact picks as jobs-index params; returns
//                                   true when terms were left out
//   chips.sourceChosen()            the picker's change action
//   chips.refreshTermCounts()       after the other filters change
//   chips.ensureLabels()            restored picks' display spellings
//   chips.destroy()                 Stimulus disconnect()

import {
  createSearchList, fieldChips, normalizeTerm, isEmptyState, CONTAINS_PREFIX
} from './search_list'

function normalizePick(value) {
  return String(value || '').trim().toLowerCase()
}

// A source's state from its URL tokens. labels: each pick's spelling as
// the URL had it (older links carry the stored spelling), until the list
// answers with the most common one.
function tokenState(tokens) {
  const state = {
    terms: [], picks: [], excludes: [], labels: new Map()
  }
  tokens.forEach((raw) => {
    const t = String(raw || '')
    if (t.startsWith(CONTAINS_PREFIX)) {
      const term = normalizeTerm(t.slice(CONTAINS_PREFIX.length))
      if (term && !state.terms.includes(term)) state.terms.push(term)
    } else {
      const pick = normalizePick(t)
      if (pick && !state.picks.includes(pick)) {
        state.picks.push(pick)
        if (t.trim() !== pick) state.labels.set(pick, t.trim())
      }
    }
  })
  return state
}

export default class JobChipFilters {
  constructor(opts) {
    this.opts = { scope: 'jobs', onChange: () => {}, onLabels: () => {}, ...opts }
    // key -> { label, terms, picks, excludes, labels: Map, termTotals: Map }
    this.sources = new Map()
    this.renderList(false)
  }

  // ---- state ----

  sourceLabel(key) {
    if (key === 'tag') return 'Tag'
    const opt = [...this.opts.picker.options].find(o => o.value === key)
    if (opt) return opt.textContent
    const [kind, rest] = key.split(/:(.*)/s)
    return kind === 'col' ? `Column #${rest}` : rest
  }

  source(key) {
    let src = this.sources.get(key)
    if (!src) {
      src = {
        label: this.sourceLabel(key),
        terms: [],
        picks: [],
        excludes: [],
        labels: new Map(), // value -> most common spelling (from the lists)
        urlLabels: new Map(), // value -> spelling a restored link carried
        termTotals: new Map()
      }
      this.sources.set(key, src)
    }
    return src
  }

  // tags[]=, columns[]=<id>:, fields[]=<enc name>: (exact or ~term values)
  initFromUrl(url) {
    const tokens = new Map()
    const add = (key, value) => {
      if (!key || !value) return
      if (!tokens.has(key)) tokens.set(key, [])
      tokens.get(key).push(value)
    }
    url.getAll('tags[]').forEach(v => add('tag', v))
    url.getAll('columns[]').forEach((raw) => {
      const [id, value] = raw.split(/:(.*)/s)
      if (/^\d+$/.test(id || '')) add(`col:${id}`, value)
    })
    url.getAll('fields[]').forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      let name = ''
      try { name = decodeURIComponent(rawKey || '') } catch (e) { name = rawKey || '' }
      name = name.trim().toLowerCase()
      if (name) add(`field:${name}`, value)
    })
    tokens.forEach((list, key) => {
      const { labels, ...state } = tokenState(list)
      if (isEmptyState(state)) return
      const src = this.source(key)
      Object.assign(src, state)
      labels.forEach((v, k) => src.urlLabels.set(k, v))
    })
  }

  // A pick's display spelling: the lists' most common one, else the one a
  // restored link carried (a pick with no jobs in range comes back from
  // the server as its bare normalized value), else the value.
  static labelOf(src, value) {
    const listed = src.labels.get(value)
    if (listed && listed !== value) return listed
    return src.urlLabels.get(value) || listed || value
  }

  // One source's tokens, as sent (terms first, then picks)
  static tokens(src) {
    return src.terms.map(t => `${CONTAINS_PREFIX}${t}`).concat(src.picks)
  }

  appendParams(p, except = null) {
    this.sources.forEach((src, key) => {
      if (key === except) return
      const [kind, rest] = key === 'tag' ? ['tag', ''] : key.split(/:(.*)/s)
      JobChipFilters.tokens(src).forEach((v) => {
        if (kind === 'tag') p.append('tags[]', v)
        else if (kind === 'col') p.append('columns[]', `${rest}:${v}`)
        else p.append('fields[]', `${encodeURIComponent(rest)}:${v}`)
      })
    })
  }

  viewParams(params) {
    const p = new URLSearchParams()
    this.appendParams(p)
    const out = params
    const keys = [['tags[]', 'tags'], ['columns[]', 'columns'], ['fields[]', 'fields']]
    keys.forEach(([param, key]) => {
      const list = p.getAll(param)
      if (list.length) out[key] = list
    })
    return out
  }

  // Exact picks in the jobs index's vocabulary: tags[] by name, job columns
  // as job_column_<id>[] (a contains match there, so a superset of the exact
  // match). Job fields have no jobs-index filter, and terms no contains
  // filter there: both are left out (returns true when anything was).
  drillParams(p) {
    let dropped = false
    this.sources.forEach((src, key) => {
      const labelFor = v => JobChipFilters.labelOf(src, v)
      if (src.terms.length) dropped = true
      if (key === 'tag') {
        src.picks.forEach(v => p.append('tags[]', labelFor(v)))
      } else if (key.startsWith('col:')) {
        src.picks.forEach(v => p.append(`job_column_${key.slice(4)}[]`, labelFor(v)))
      } else if (src.picks.length) {
        dropped = true
      }
    })
    return dropped
  }

  chips() {
    const out = []
    this.sources.forEach((src, key) => {
      out.push(...fieldChips(src, {
        label: src.label,
        nounOne: 'value',
        nounMany: 'values',
        labelFor: v => JobChipFilters.labelOf(src, v),
        termTotals: src.termTotals,
        onChange: state => this.setState(key, state),
        onOpen: () => this.openSource(key)
      }))
    })
    return out
  }

  setState(key, state) {
    const src = this.source(key)
    if (this.list && this.opts.picker.value === key) {
      this.list.termTotals.forEach((n, t) => src.termTotals.set(t, n))
    }
    Object.assign(src, { terms: state.terms, picks: state.picks.map(normalizePick), excludes: [] })
    if (isEmptyState(src)) this.sources.delete(key)
    if (this.list && this.opts.picker.value === key) this.list.setState(src)
    this.opts.onChange()
  }

  // ---- options endpoints ----

  fetchOptions(key, q, state) {
    const [kind, rest] = key === 'tag' ? ['tag', ''] : key.split(/:(.*)/s)
    const p = this.opts.baseParams()
    p.set('list', '1')
    if (this.opts.scope === 'results') p.set('scope', 'results')
    if (q) p.set('q', q)
    state.picks.forEach(v => p.append('selected[]', v))
    this.appendParams(p, key)
    let url = this.opts.urls.tag
    if (kind === 'col') {
      url = this.opts.urls.column
      p.set('column_id', rest)
    } else if (kind === 'field') {
      url = this.opts.urls.field
      p.set('field', rest)
    }
    return fetch(`${url}?${p}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
  }

  remember(src, data) {
    (data.options || []).forEach((o) => { if (o.label) src.labels.set(o.value, o.label) })
  }

  // "rush · 12 values" counts behind the term chips, under the current
  // filters (the server caches the grouped rows, q only filters them)
  // Each refresh bumps the source's sequence; a slower, older response
  // that lands after a newer one is dropped instead of leaving a stale count.
  refreshTermCounts() {
    this.sources.forEach((src, key) => {
      if (!this.termSeqs) this.termSeqs = new Map()
      const seq = (this.termSeqs.get(key) || 0) + 1
      this.termSeqs.set(key, seq)
      src.termTotals.clear()
      if (!src.terms.length) return
      Promise.all(src.terms.map(t => this.fetchOptions(key, t, src).then(data => [t, data])))
        .then((pairs) => {
          if (seq !== this.termSeqs.get(key)) return
          pairs.forEach(([t, data]) => {
            this.remember(src, data)
            src.termTotals.set(t, data.total_options || 0)
          })
          this.opts.onLabels()
        })
        .catch(() => {})
    })
  }

  ensureLabels() {
    this.sources.forEach((src, key) => {
      if (!src.picks.some(v => !src.labels.has(v) && !src.urlLabels.has(v))) return
      this.fetchOptions(key, '', src).then((data) => {
        this.remember(src, data)
        this.opts.onLabels()
      }).catch(() => {})
    })
  }

  // ---- the value search list ----

  sourceChosen() {
    this.renderList(true)
  }

  openSource(key) {
    this.opts.picker.value = key
    this.renderList(true)
  }

  renderList(open) {
    if (this.list) this.list.destroy()
    const key = this.opts.picker.value
    if (!key) {
      this.list = createSearchList(this.opts.anchor, {
        label: 'Value', fetchOptions: () => Promise.resolve({ options: [] })
      })
      this.list.setDisabled(true, 'Pick a filter first')
      return
    }
    const existing = this.sources.get(key)
    const label = existing ? existing.label : this.sourceLabel(key)
    const labels = existing ? existing.labels : new Map()
    this.list = createSearchList(this.opts.anchor, {
      label,
      placeholder: 'Any value',
      searchPlaceholder: `Search ${label}`,
      nounOne: 'value',
      nounMany: 'values',
      countNoun: 'jobs',
      outOfRangeText: 'no jobs in range',
      supportsTerms: true,
      supportsExcludes: false,
      state: existing,
      labels,
      termTotals: existing ? existing.termTotals : undefined,
      fetchOptions: (q, state) => this.fetchOptions(key, q, state).then((data) => {
        const src = this.sources.get(key)
        if (src) this.remember(src, data)
        return data
      }),
      onApply: (state) => {
        const src = this.source(key)
        // a new source's labels were collected by the list
        labels.forEach((v, k) => src.labels.set(k, v))
        this.setState(key, state)
      }
    })
    if (open) this.list.open()
  }

  destroy() {
    if (this.list) this.list.destroy()
    this.list = null
  }
}
